import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useUserStats } from '../context/UserStatsContext';
import { Button, FeedbackModal, PracticeCompleteModal, PracticeTimer, LoadingWhale } from '../components/common';
import { getReadingContent } from '../services/api';
import { ReadingContent, SessionResult } from '../types';

// Fallback content
const fallbackContent: Record<string, ReadingContent> = {
  basic: {
    id: 'basic-1',
    title: 'My Pet Cat',
    content: 'I have a cat. My cat is orange. Her name is Sunny. Sunny likes to sleep. She sleeps on my bed. Sunny is my best friend!',
    complexity: 'basic',
    questions: [
      {
        question: 'What color is the cat?',
        options: ['Blue', 'Orange', 'Black', 'White'],
        correctIndex: 1,
      },
      {
        question: 'What is the cat\'s name?',
        options: ['Fluffy', 'Sunny', 'Kitty', 'Max'],
        correctIndex: 1,
      },
      {
        question: 'Where does Sunny sleep?',
        options: ['On the floor', 'In a box', 'On the bed', 'Outside'],
        correctIndex: 2,
      },
    ],
  },
  intermediate: {
    id: 'intermediate-1',
    title: 'A Day at the Beach',
    content: 'Last summer, my family went to the beach. We woke up early and packed our bags. Mom made sandwiches for lunch. At the beach, I built a big sandcastle with my brother. We swam in the ocean and looked for seashells. The water was cold but fun! We stayed until sunset. It was a perfect day.',
    complexity: 'intermediate',
    questions: [
      {
        question: 'When did the family go to the beach?',
        options: ['Last winter', 'Last summer', 'Yesterday', 'Last week'],
        correctIndex: 1,
      },
      {
        question: 'Who made the sandwiches?',
        options: ['Dad', 'Brother', 'Mom', 'Grandmother'],
        correctIndex: 2,
      },
      {
        question: 'What did the narrator build?',
        options: ['A boat', 'A sandcastle', 'A tent', 'A bridge'],
        correctIndex: 1,
      },
      {
        question: 'How was the water?',
        options: ['Warm', 'Hot', 'Cold', 'Perfect'],
        correctIndex: 2,
      },
    ],
  },
  advanced: {
    id: 'advanced-1',
    title: 'The Mystery of the Missing Cookies',
    content: 'Detective Emma had a new case to solve. Someone had eaten all the cookies from the kitchen jar! She looked for clues carefully. First, she noticed cookie crumbs leading to the living room. Then she found chocolate smudges on the couch. Her little brother Tommy was sitting there, watching TV. His face had chocolate all around his mouth! "Case solved," Emma said with a smile. Tommy giggled and promised to share next time.',
    complexity: 'advanced',
    questions: [
      {
        question: 'What was missing from the kitchen?',
        options: ['Milk', 'Bread', 'Cookies', 'Apples'],
        correctIndex: 2,
      },
      {
        question: 'Where did the crumbs lead?',
        options: ['To the bedroom', 'To the garden', 'To the living room', 'To the bathroom'],
        correctIndex: 2,
      },
      {
        question: 'What did Emma find on the couch?',
        options: ['Cookie crumbs', 'Chocolate smudges', 'Juice stains', 'Pencil marks'],
        correctIndex: 1,
      },
      {
        question: 'Who ate the cookies?',
        options: ['Emma', 'Mom', 'Dad', 'Tommy'],
        correctIndex: 3,
      },
      {
        question: 'What did Tommy promise?',
        options: ['To clean up', 'To share next time', 'To buy more cookies', 'To say sorry'],
        correctIndex: 1,
      },
    ],
  },
};

const ReadingPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useUser();
  const { startSession, endSession, stats, loading: statsLoading } = useUserStats();

  const [content, setContent] = useState<ReadingContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [answers, setAnswers] = useState<number[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [exerciseCount, setExerciseCount] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null);
  const [isPracticing, setIsPracticing] = useState(true);

  const complexity = profile?.complexity || 'basic';

  // Start session when stats are loaded
  useEffect(() => {
    if (stats && !statsLoading) {
      startSession('reading');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, statsLoading]);

  const loadContent = async () => {
    setIsLoading(true);
    setAnswers([]);
    setScore(0);

    try {
      const data = await getReadingContent(complexity);
      setContent(data);
    } catch (err) {
      // Use fallback
      setContent(fallbackContent[complexity]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexity]);

  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleCheck = async () => {
    if (!content || !allAnswered) return;

    setIsChecking(true);

    // Calculate score locally (more reliable than API for kids app)
    let correct = 0;
    content.questions.forEach((q, i) => {
      console.log(`[DEBUG] Question ${i}: user answered ${answers[i]}, correct is ${q.correctIndex}`);
      if (answers[i] === q.correctIndex) correct++;
    });
    const calculatedScore = Math.round((correct / content.questions.length) * 100);
    console.log(`[DEBUG] Score: ${correct}/${content.questions.length} = ${calculatedScore}%`);

    setScore(calculatedScore);
    setIsChecking(false);
    setShowFeedback(true);
    setExerciseCount((prev) => prev + 1);
  };

  const handleNext = () => {
    setShowFeedback(false);
    loadContent();
  };

  const handleTryAgain = () => {
    setShowFeedback(false);
    setAnswers([]);
    setScore(0);
  };

  const handleGoBack = async () => {
    setIsPracticing(false);
    const sessionRes = await endSession(score);
    if (sessionRes) {
      setSessionResult(sessionRes);
      setShowProgressModal(true);
    } else {
      navigate('/app');
    }
  };

  const handleContinuePractice = () => {
    setShowProgressModal(false);
    setSessionResult(null);
    setIsPracticing(true);
    startSession('reading');
  };

  const handleGoHome = () => {
    setShowProgressModal(false);
    navigate('/app');
  };

  const allAnswered = content ? answers.length === content.questions.length && !answers.includes(undefined as any) : false;

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900">
      {/* Header - Always rendered to keep timer running */}
      <header className="bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
          >
            <span className="text-2xl">{'\u2190'}</span>
            <span className="font-bold">Done</span>
          </button>
          <h1 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
            <span>{'\u{1F4DA}'}</span> Reading
          </h1>
          <PracticeTimer isActive={isPracticing} />
        </div>
      </header>

      {/* Loading state */}
      {isLoading || !content ? (
        <LoadingWhale message="Loading story..." />
      ) : (
      /* Main content */
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Exercise counter */}
        <div className="text-center mb-4">
          <span className="text-gray-500 dark:text-gray-400">Story {exerciseCount + 1}</span>
        </div>

        {/* Story section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 text-center">{content.title}</h2>

          <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl p-6">
            <p className="text-lg text-gray-800 dark:text-gray-200 leading-relaxed">{content.content}</p>
          </div>
        </div>

        {/* Questions section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 text-center">
            Answer the Questions
          </h3>

          <div className="space-y-6">
            {content.questions.map((question, qIndex) => (
              <div key={qIndex} className="border-b-2 border-gray-100 dark:border-slate-700 pb-6 last:border-0">
                <p className="font-bold text-gray-800 dark:text-white mb-4 text-lg">
                  {qIndex + 1}. {question.question}
                </p>
                <div className="space-y-3">
                  {question.options.map((option, oIndex) => {
                    const letters = ['A', 'B', 'C', 'D'];
                    const isSelected = answers[qIndex] === oIndex;
                    return (
                      <button
                        key={oIndex}
                        onClick={() => handleAnswerSelect(qIndex, oIndex)}
                        className={`
                          w-full p-4 rounded-2xl border-3 text-left transition-all duration-200 flex items-center gap-4
                          ${isSelected
                            ? 'border-emerald-500 bg-emerald-100 dark:bg-emerald-900/40 shadow-md'
                            : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                          }
                        `}
                      >
                        <span className={`
                          w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0
                          ${isSelected
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white dark:bg-slate-600 border-2 border-gray-300 dark:border-slate-500 text-gray-600 dark:text-gray-200'
                          }
                        `}>
                          {letters[oIndex]}
                        </span>
                        <span className={`text-lg ${isSelected ? 'font-bold text-emerald-800 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-200'}`}>
                          {option}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Button
              onClick={handleCheck}
              variant="success"
              size="large"
              fullWidth
              disabled={!allAnswered || isChecking}
            >
              {isChecking ? 'Checking...' : 'Check Answers'}
            </Button>
          </div>
        </div>

        {/* Skip button */}
        <div className="text-center mt-6">
          <Button onClick={handleNext} variant="secondary" size="medium">
            Skip this story
          </Button>
        </div>
      </main>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={score >= 70 ? 'success' : 'error'}
        title={score === 100 ? 'Perfect!' : score >= 70 ? 'Well Done!' : 'Keep Reading!'}
        message={
          score === 100
            ? 'You understood the story perfectly!'
            : score >= 70
            ? 'Great reading! Let\'s see the answers:'
            : 'Let\'s see what you missed:'
        }
        score={score}
        buttonText="Next Story"
        onButtonClick={handleNext}
        quizResult={score < 100 && content ? { questions: content.questions, userAnswers: answers } : undefined}
        onTryAgain={handleTryAgain}
      />

      {/* Practice Complete Modal */}
      <PracticeCompleteModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        result={sessionResult}
        onContinue={handleContinuePractice}
        onGoHome={handleGoHome}
      />
    </div>
  );
};

export default ReadingPage;
